import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, VStack, IconButton, Text, Portal, useColorModeValue } from '@chakra-ui/react';
import { Tooltip } from './components/Tip';
import { FiCompass, FiX } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDraggableFloat, computeMenuPlacement } from '../../hooks/useDraggableFloat';
import {
    LAUNCHER, menuMaxH, LauncherEdgeTab, LauncherHideButton, useLauncherClickOutside,
    launcherFabMotion, useLauncherReducedMotion,
} from '../../components/floating/LauncherChrome';
import { useHideToEdge } from '../../hooks/useHideToEdge';
import { useModulePermissions } from '../../contexts/ModulePermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { LayoutChromeContext } from '../../components/LayoutChromeContext';
import { calendarsModuleConfig } from './moduleConfig';
import {
    RESET_FLOATING_PANELS_EVENT,
    CAL_NAV_FAB_POS_KEY,
    CAL_NAV_FAB_HIDDEN_KEY,
    CAL_NAV_OPEN_KEY,
} from './utils/floatingPanels';

const MAIN_CALENDAR_PATH = '/calendars/main';

/**
 * The calendar module's PAGE NAVIGATION as its own floating launcher — the
 * breadcrumb strip's links (Bird's Eye View · My Calendars · Life Goals ·
 * New Calendar · Admin · What's New), deliberately SEPARATE from the
 * calendar-controls menu (FloatingCalendarMenu) so "where can I go" never
 * mixes with "what does this calendar do" (Tom's call, 2026-08-15).
 *
 * Mount it on EVERY calendar-module page. It hides the shell module-nav
 * launcher (formerly ModuleBreadcrumb) itself through LayoutChromeContext —
 * safe no-op default when the page renders outside the layout — so mounting
 * it IS the module-navigation replacement; the two never coexist.
 *
 * Built per vault/how-to/draggable-floating-launchers.md: draggable FAB
 * (RIGHT edge, vertically centred — the controls menu owns top-left), menu
 * placed with computeMenuPlacement in the same Portal, eye-off hide with a
 * right-edge tab, reset-broadcast listener, keys in utils/floatingPanels.ts,
 * z1315. Every size comes from LauncherChrome (2026-08-27, § One file owns the
 * numbers) — declared under `### Shared files` in this module's CLAUDE.md.
 */

const FAB_SIZE = LAUNCHER.FAB_SIZE;
const MENU_W = LAUNCHER.MENU_W;

/** Right edge, vertically centred (min 84px clear of the navbar) — feeds the hook AND the reset. */
const calendarNavHome = (vw: number, vh: number) => ({
    left: vw - FAB_SIZE - 16,
    top: Math.max(84, Math.round(vh / 2 - FAB_SIZE / 2)),
});

export const FloatingCalendarNav: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const menuBg = useColorModeValue('white', 'gray.800');
    const menuBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
    const headingColor = useColorModeValue('gray.600', 'whiteAlpha.700');
    const itemColor = useColorModeValue('gray.800', 'whiteAlpha.900');

    // Mounting this launcher replaces the breadcrumb strip on the page.
    const { setBreadcrumbHidden } = useContext(LayoutChromeContext);
    useEffect(() => {
        setBreadcrumbHidden(true);
        return () => setBreadcrumbHidden(false);
    }, [setBreadcrumbHidden]);

    // Permission-filter the nav items the same way ModuleBreadcrumb does
    // (backend-resolved permission strings vs the user's own). useAuth throws
    // outside an AuthProvider (tests, public shells) and AuthContext is not
    // exported from that tenant-drifted file — guarded call, same rationale as
    // FloatingCalendarMenu.
    const { getPermissions } = useModulePermissions();
    let user: any = null;
    try {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        user = useAuth().user;
    } catch {
        user = null;
    }
    const canSee = (level?: string): boolean => {
        if (!level) return true;
        const perms = getPermissions(calendarsModuleConfig.id, level);
        if (!perms || perms.length === 0) return true;
        return !!user?.permissions?.some((p: string) => perms.includes(p));
    };
    // "My main calendar" is the one-click shortcut and always leads; the rest are A–Z (rule 45 — a
    // launcher menu is a list you pick from, and nobody has curated this one).
    const navItems = calendarsModuleConfig.navigation
        .filter((item) => canSee(item.permissionLevel))
        .sort((a, b) =>
            (Number(b.path === MAIN_CALENDAR_PATH) - Number(a.path === MAIN_CALENDAR_PATH)) ||
            a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }));

    // ── open state (persisted, like the other launchers) ─────────────────────
    const [isExpanded, setIsExpandedState] = useState<boolean>(() => {
        try {
            return localStorage.getItem(CAL_NAV_OPEN_KEY) === '1';
        } catch {
            return false;
        }
    });
    const setIsExpanded = (v: boolean | ((prev: boolean) => boolean)) => {
        setIsExpandedState((prev) => {
            const next = typeof v === 'function' ? v(prev) : v;
            try {
                localStorage.setItem(CAL_NAV_OPEN_KEY, next ? '1' : '0');
            } catch {
                /* noop */
            }
            return next;
        });
    };

    // ── FAB drag (top-right home; the controls menu owns top-left) ───────────
    const {
        position: fabPos,
        setPosition: setFabPos,
        dragHandlers: fabDrag,
        wasJustDragged,
    } = useDraggableFloat({
        storageKey: CAL_NAV_FAB_POS_KEY,
        size: FAB_SIZE,
        edgePad: LAUNCHER.EDGE_PAD,
        defaultPosition: calendarNavHome,
    });

    // ── hide-to-edge-tab (right edge — the FAB's home side) ──────────────────
    // Hide-to-edge on the SAME storage key as before (useHideToEdge reads/writes the "1"/"0"
    // flag) — adopting the hook gains the edge tab's announce pulse and the shared reset
    // self-clear, instead of a third hand-rolled copy of the hidden state (2026-09-01).
    const { hidden: fabHidden, setHidden: setFabHidden, justHidden } = useHideToEdge(CAL_NAV_FAB_HIDDEN_KEY, RESET_FLOATING_PANELS_EVENT);
    const reducedMotion = useLauncherReducedMotion();
    const hideFab = (hidden: boolean) => {
        setFabHidden(hidden);
        if (hidden) setIsExpanded(false);
    };

    // ── reset broadcast: snap home, un-hide, close. Self-clearing (2026-08-15
    // standard): remove our OWN keys so a dispatch-only reset survives reload
    // (setFabPos never persists). ────────────────────────────────────────────
    useEffect(() => {
        const onReset = () => {
            try {
                localStorage.removeItem(CAL_NAV_FAB_POS_KEY);
                localStorage.removeItem(CAL_NAV_FAB_HIDDEN_KEY);
                localStorage.removeItem(CAL_NAV_OPEN_KEY);
            } catch { /* noop */ }
            setIsExpanded(false);
            setFabHidden(false);
            setFabPos(calendarNavHome(window.innerWidth, window.innerHeight));
        };
        window.addEventListener(RESET_FLOATING_PANELS_EVENT, onReset);
        return () => window.removeEventListener(RESET_FLOATING_PANELS_EVENT, onReset);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── click-outside closes the menu (pointerdown — a finger tap counts too) ──
    const closeMenu = useCallback(() => setIsExpanded(false), []); // eslint-disable-line react-hooks/exhaustive-deps
    useLauncherClickOutside(isExpanded, ['.calendar-nav-fab', '.calendar-nav-panel'], closeMenu);

    const placement = computeMenuPlacement(
        { left: fabPos.left, top: fabPos.top, size: FAB_SIZE },
        { w: typeof window !== 'undefined' ? window.innerWidth : 1200, h: typeof window !== 'undefined' ? window.innerHeight : 800 },
        { preferredWidth: MENU_W, estimatedHeight: 420, gap: LAUNCHER.MENU_GAP },
        LAUNCHER.EDGE_PAD,
    );

    return (
        <Portal>
            {/* ── Edge tab — ALL that renders while hidden. Never remove it. ── */}
            {fabHidden && (
                <LauncherEdgeTab
                    announce={justHidden}
                    edge="right"
                    top={fabPos.top}
                    bg="purple.500"
                    zIndex={1315}
                    label="Show the calendar pages menu again"
                    onClick={() => hideFab(false)}
                />
            )}

            {/* ── FAB ── */}
            {!fabHidden && (
                <Box className="calendar-nav-fab" position="fixed" top={`${fabPos.top}px`} left={`${fabPos.left}px`} zIndex={1315}>
                    <Tooltip label="Calendar pages — your main calendar, My Calendars, Life Goals and more. Drag to move, tap to open." placement="left">
                        <IconButton
                            aria-label="Open the calendar pages menu — drag to move"
                            icon={isExpanded ? <FiX size={22} /> : <FiCompass size={22} />}
                            size="lg"
                            w={`${FAB_SIZE}px`}
                            h={`${FAB_SIZE}px`}
                            borderRadius="full"
                            colorScheme="purple"
                            {...fabDrag}
                            {...launcherFabMotion(reducedMotion)}
                            onClick={() => {
                                if (wasJustDragged()) return;
                                setIsExpanded((v) => !v);
                            }}
                        />
                    </Tooltip>
                    <LauncherHideButton
                        fabSize={FAB_SIZE}
                        wasJustDragged={wasJustDragged}
                        label="Hide the calendar pages menu — a small tab stays on the right edge to bring it back. The footer's ↻ Reset floating panels also restores it."
                        onClick={() => hideFab(true)}
                    />
                </Box>
            )}

            {/* ── Menu — viewport-relative via computeMenuPlacement ── */}
            {!fabHidden && isExpanded && (
                <Box
                    className="calendar-nav-panel"
                    position="fixed"
                    {...(placement.top !== undefined ? { top: `${placement.top}px` } : {})}
                    {...(placement.bottom !== undefined ? { bottom: `${placement.bottom}px` } : {})}
                    left={`${placement.left}px`}
                    width={`${placement.width}px`}
                    zIndex={1315}
                    bg={menuBg}
                    border="1px solid"
                    borderColor={menuBorder}
                    borderRadius="xl"
                    boxShadow="0 20px 60px rgba(0,0,0,0.35)"
                    p={3}
                    // placement.maxHeight = the room on the chosen side, so the
                    // menu is always fully on screen wherever the FAB was dragged
                    // and opening it never resizes/scrolls the page (2026-08-20).
                    maxH={menuMaxH(placement)}
                    overflowY="auto"
                    overscrollBehavior="contain"
                >
                    <VStack align="stretch" spacing={2}>
                        <Text fontSize="sm" fontWeight="700" color={headingColor} px={1}>
                            Calendar pages
                        </Text>
                        {navItems.map((item) => {
                            const isCurrent = location.pathname === item.path;
                            return (
                                <Tooltip key={item.path} label={item.path === MAIN_CALENDAR_PATH ? 'Open the calendar you chose as your main one — or choose it, if you have not yet' : `Go to ${item.label}`} placement="left">
                                    <Button
                                        size="lg"
                                        minH="48px"
                                        fontSize="md"
                                        justifyContent="flex-start"
                                        variant={isCurrent ? 'solid' : 'ghost'}
                                        colorScheme={isCurrent ? 'purple' : undefined}
                                        color={isCurrent ? 'white' : itemColor}
                                        _hover={{ bg: isCurrent ? 'purple.600' : 'rgba(128, 90, 213, 0.12)' }}
                                        borderRadius="lg"
                                        whiteSpace="normal"
                                        textAlign="left"
                                        onClick={() => {
                                            setIsExpanded(false);
                                            navigate(item.path);
                                        }}
                                    >
                                        {item.icon} {item.label}
                                    </Button>
                                </Tooltip>
                            );
                        })}
                    </VStack>
                </Box>
            )}
        </Portal>
    );
};

export default FloatingCalendarNav;
